import {
    FormField,
    Select,
    SelectProps,
} from '@amzn/awsui-components-react-v3';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    selectFeatures,
    selectIsLoaded as selectIsFeaturesLoaded,
} from '../../../../../common/store/slices/featureSlice';
import { checkFeature } from '../../../../../common/utils/featureFlag';
import {
    ActivityLMSType,
    ActivityModality,
    ActivityViltType,
} from '../../Common/Common';
import { ActivityData } from '../../../../interfaces/activity';

export const LMS_TYPE_DESCRIPTION = 'The LMS used for the class.';

export type LMSTypeValues =
    | ActivityLMSType.Kiku
    | ActivityLMSType.MyClass
    | ActivityLMSType.CustomerLMS
    | ActivityLMSType.SkillBuilder;

interface LMSTypeOption extends SelectProps.Option {
    readonly label: LMSTypeValues;
    readonly value: string;
}

const lmsTypeOptions: Array<LMSTypeOption> = [
    {
        label: ActivityLMSType.Kiku,
        value: ActivityLMSType.Kiku,
    },
    {
        label: ActivityLMSType.MyClass,
        value: ActivityLMSType.MyClass,
    },
    {
        label: ActivityLMSType.CustomerLMS,
        value: ActivityLMSType.CustomerLMS,
    },
    {
        label: ActivityLMSType.SkillBuilder,
        value: ActivityLMSType.SkillBuilder,
    },
];

const lmsTypeOptionsLookup = lmsTypeOptions.reduce(
    (acc, opt) => {
        acc[opt.label] = opt;
        return acc;
    },
    {} as {
        [key in LMSTypeValues]: LMSTypeOption;
    },
);

interface SelectLMSTypeProps {
    handleFieldEvent: (values: Partial<ActivityData>) => void;
    formValues: {
        activity_modality: string;
        lms_type: string;
        v_ilt_type?: string | null;
        classrooms_arn?: string | null;
        classrooms_status?: string | null;
    };
    errors: {
        activity_modality: string;
        lms_type: string;
        v_ilt_type: string;
        classrooms_arn?: String;
        classrooms_status?: String;
    };
}

const SelectLMSType = ({
    handleFieldEvent,
    formValues,
    errors,
}: SelectLMSTypeProps) => {
    const featureFlags = useSelector(selectFeatures);
    const featuresLoaded = useSelector(selectIsFeaturesLoaded);

    const isVILT = formValues.activity_modality === ActivityModality.vILT;
    const isHybrid = formValues.activity_modality === ActivityModality.Hybrid;
    const isViltOrHybrid = isVILT || isHybrid;

    useEffect(() => {
        handleViltTypeChange(formValues.lms_type);
    }, [formValues.lms_type]);

    const handleCustomerLmsPlatformChange = (lmsType) => {
        // Clear customer_lms_platform when changing away from CustomerLMS
        if (
            formValues.lms_type === ActivityLMSType.CustomerLMS &&
            lmsType !== ActivityLMSType.CustomerLMS
        ) {
            handleFieldEvent({
                customer_lms_platform: null,
            });
        }
    };

    const handleViltTypeChange = (lmsType) => {
        if (lmsType !== formValues.lms_type || !formValues.v_ilt_type) {
            let v_ilt_type = formValues.v_ilt_type || '';
            // select vilt type if only one option available
            switch (lmsType) {
                case ActivityLMSType.MyClass:
                    v_ilt_type = ActivityViltType.WebEx;
                    break;
                case ActivityLMSType.Kiku:
                    v_ilt_type = ActivityViltType.WebEx;
                    break;
                case ActivityLMSType.SkillBuilder:
                    v_ilt_type = ActivityViltType.WebEx;
                    break;
                case ActivityLMSType.CustomerLMS:
                    v_ilt_type = ActivityViltType.WebEx;
                    break;
                default:
                    break;
            }
            handleFieldEvent({
                v_ilt_type,
            });
        }
    };
    return (
        <FormField
            label={<span>LMS type</span>}
            errorText={errors?.lms_type}
            data-testid="formField-EditLMSType"
        >
            <Select
                placeholder="Select LMS type"
                options={lmsTypeOptions}
                selectedOption={
                    formValues.lms_type
                        ? lmsTypeOptionsLookup[
                              formValues.lms_type as LMSTypeValues
                          ]
                        : null
                }
                onChange={(e) => {
                    const newLmsType = e.detail.selectedOption.label;
                    handleFieldEvent({
                        lms_type: newLmsType,
                    });
                    handleCustomerLmsPlatformChange(newLmsType);
                    isViltOrHybrid && handleViltTypeChange(newLmsType);
                }}
                data-testid="EditLMSType"
            />
        </FormField>
    );
};

export default SelectLMSType;
